import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormField, FormItem } from "@shadcn/components/ui/form";
import { cn } from "@shadcn/lib/utils";
import { Input } from "@shadcn/components/ui/input";
import { Button } from "@shadcn/components/ui/button";
import { Content } from "@forms/form-render/components/Fields/Content";
import InputMask from "react-input-mask";
import { VonageVerifyCodeEntryProps } from "./VonageVerifyCodeEntry.types";
import { enquiryApi } from "@/data/enquiries/api";
import { AlertVariant } from "@schema-types/types/JourneySchema";
import { useFormStepPreSubmission } from "@forms/form-render/components/Steps/FormStep/FormStep";

const codeInfoConfig = {
  alertVariant: AlertVariant.Info,
  icon: "Info",
  content:
    "Enter the code we sent to your email. Check your spam folder if you don't see it.",
};

const MASK = "9-9-9-9";
const PLACEHOLDER = "_-_-_-_";

type CodeStatus = "expired" | "invalid" | null;

export const VonageVerifyCodeEntry = ({
  name,
  required,
  disabled,
  enquiryId,
  currentStepId,
}: VonageVerifyCodeEntryProps) => {
  const { control, setValue, getValues, setError } = useFormContext();
  const registerPreSubmissionCall = useFormStepPreSubmission();
  const canVerifyViaApi = Boolean(enquiryId && currentStepId);
  const [codeStatus, setCodeStatus] = useState<CodeStatus>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const fieldName = `${name}.code`;

  useEffect(() => {
    if (!canVerifyViaApi) return () => {};
    const unregister = registerPreSubmissionCall(async () => {
      const value = getValues(fieldName);
      if (!value || typeof value !== "string") return true;
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length < 4) return true;
      try {
        const result = await enquiryApi.verifyCode({
          enquiryId: enquiryId!,
          currentStepId: currentStepId!,
          code: digitsOnly,
        });
        if (!result.valid) {
          if (result.status === "expired") {
            setCodeStatus("expired");
            setError(fieldName, { message: "Code has expired" });
            return false;
          }
          if (result.status === "invalid_code") {
            setCodeStatus("invalid");
            setError(fieldName, { message: "The code you entered is incorrect." });
            return false;
          }
          setCodeStatus("expired");
          setError(fieldName, { message: "Invalid or expired code. Please check the code and try again." });
          return false;
        }
        setCodeStatus(null);
        return true;
      } catch {
        setCodeStatus(null);
        setError(fieldName, { message: "Unable to verify code. Please try again." });
        return false;
      }
    });
    return unregister;
  }, [canVerifyViaApi, enquiryId, currentStepId, fieldName, getValues, registerPreSubmissionCall, setError]);

  const maskValue = (raw: string) => {
    const digits = (raw ?? "").replace(/\D/g, "").slice(0, 4);
    return digits.length === 4 ? `${digits[0]}-${digits[1]}-${digits[2]}-${digits[3]}` : digits;
  };

  const handleResend = async () => {
    if (!enquiryId || !currentStepId || resendLoading) return;
    setResendLoading(true);
    try {
      const result = await enquiryApi.resendCode({ enquiryId, currentStepId });
      if (result.success) {
        setCodeStatus(null);
        setValue(`${name}.code`, "", { shouldValidate: false });
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <FormField
      control={control}
      name={`${name}.code`}
      rules={{
        required: required ? "Verification code is required" : false,
        pattern: {
          value: /^\d{4}$/,
          message: "Please enter the 4-digit verification code",
        },
      }}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="mb-2">
            <Content config={codeInfoConfig} />
          </div>
          {fieldState.error?.message && (
            <p className={cn("text-sm font-medium text-destructive text-center")}>
              {fieldState.error.message}
            </p>
          )}
          <FormControl>
            <InputMask
              mask={MASK}
              value={maskValue(field.value ?? "")}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 4);
                field.onChange(digitsOnly);
                setCodeStatus(null);
              }}
              onBlur={field.onBlur}
              disabled={disabled}
              maskChar="_"
            >
              {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                <Input
                  {...inputProps}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  placeholder={PLACEHOLDER}
                  disabled={disabled}
                  aria-label="Verification code"
                  className="w-[11ch] tabular-nums text-center m-auto"
                  style={{ fontSize: "1.5rem" }}
                />
              )}
            </InputMask>
          </FormControl>
          {codeStatus === "expired" && (
            <div className="flex justify-center mt-2">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleResend}
                disabled={resendLoading || disabled}
              >
                {resendLoading ? "Sending…" : "Resend"}
              </Button>
            </div>
          )}
        </FormItem>
      )}
    />
  );
};
